import type { FirebaseOptions } from 'firebase/app';

interface FirebaseConfigWithMeta extends FirebaseOptions {
  useEmulator: boolean;
  mode: string;
  fallbackKeys: string[];
}

const FALLBACKS: FirebaseOptions & { measurementId?: string } = {
  apiKey: 'test-api-key',
  authDomain: 'test.firebaseapp.com',
  projectId: 'social-work-test-prep-academy',
  storageBucket: 'test.appspot.com',
  messagingSenderId: '123456789',
  appId: '1:123456789:web:abcdef',
  measurementId: 'G-ABCDEF123'
};

let cachedConfig: FirebaseConfigWithMeta | null = null;

const getRuntimeEnv = (): Record<string, string | undefined> => {
  if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env) {
    return (import.meta as { env: Record<string, string | undefined> }).env;
  }

  return process.env as Record<string, string | undefined>;
};

export const readFirebaseEnvValue = (key: string): string | undefined => {
  const runtimeEnv = getRuntimeEnv();
  if (key in runtimeEnv && runtimeEnv[key] !== undefined) {
    return runtimeEnv[key];
  }

  if (process.env[key] !== undefined) {
    return process.env[key];
  }

  return undefined;
};

const resolveConfig = (): FirebaseConfigWithMeta => {
  if (cachedConfig) {
    return cachedConfig;
  }

  const fallbackKeys: string[] = [];
  const ensureValue = (envKey: string, fallbackKey: keyof typeof FALLBACKS): string => {
    const value = readFirebaseEnvValue(envKey);
    if (value && value.trim().length > 0) {
      return value;
    }

    fallbackKeys.push(envKey);
    return String(FALLBACKS[fallbackKey] ?? '');
  };

  const isTestEnvironment = process.env.NODE_ENV === 'test';

  const options: FirebaseOptions = {
    apiKey: ensureValue('VITE_FIREBASE_API_KEY', 'apiKey'),
    // authDomain — SAFE-STAGING CUTOVER (see docs/auth-cutover.md).
    //
    // Today this is `social-work-test-prep-academy.firebaseapp.com` (a FOREIGN
    // origin vs the Vercel-served app). On Safari / iOS-WebViews / in-app
    // browsers that partition third-party storage, `getRedirectResult()` then
    // returns null and social sign-in never completes.
    //
    // vercel.json now reverse-proxies `/__/auth/*` and `/__/firebase/*` to
    // firebaseapp.com, so the app's OWN domain can serve Firebase's auth
    // handler. That proxy is INERT until authDomain is flipped to the app's
    // custom domain — which is done ONLY by the human, via the Vercel env var
    // VITE_FIREBASE_AUTH_DOMAIN (NOT by editing the committed .env), after the
    // domain is added under Firebase Auth → Authorized domains. Do NOT change
    // the committed value here; that would break prod until DNS + authorized
    // domains are ready.
    authDomain: ensureValue('VITE_FIREBASE_AUTH_DOMAIN', 'authDomain'),
    projectId: ensureValue('VITE_FIREBASE_PROJECT_ID', 'projectId'),
    storageBucket: ensureValue('VITE_FIREBASE_STORAGE_BUCKET', 'storageBucket'),
    messagingSenderId: ensureValue('VITE_FIREBASE_MESSAGING_SENDER_ID', 'messagingSenderId'),
    appId: ensureValue('VITE_FIREBASE_APP_ID', 'appId'),
    measurementId: ensureValue('VITE_FIREBASE_MEASUREMENT_ID', 'measurementId')
  };

  const emulatorFlag = readFirebaseEnvValue('VITE_USE_EMULATOR');
  const useEmulator = emulatorFlag === 'true';

  const mode = (readFirebaseEnvValue('MODE') || process.env.NODE_ENV || (isTestEnvironment ? 'test' : 'development')) ?? 'development';

  cachedConfig = {
    ...options,
    useEmulator,
    mode,
    fallbackKeys
  };

  return cachedConfig;
};

export const getFirebaseConfig = (): FirebaseConfigWithMeta => resolveConfig();

export const shouldUseFirebaseEmulators = (): boolean => getFirebaseConfig().useEmulator;

export const isProductionRuntime = (): boolean => {
  const config = getFirebaseConfig();
  return config.mode === 'production' || process.env.NODE_ENV === 'production';
};

export const assertValidFirebaseConfig = (): void => {
  const config = getFirebaseConfig();
  if (!isProductionRuntime()) {
    return;
  }

  if (config.fallbackKeys.length > 0) {
    const missing = config.fallbackKeys.join(', ');
    throw new Error(`Missing Firebase environment variables for production build: ${missing}`);
  }
};

export const resetCachedFirebaseConfig = () => {
  cachedConfig = null;
};
